// Bridge exposed by the Odoo module `cl_livechat_customize` (window.clLivechat).
type ClLivechatApi = {
  isReady: () => boolean;
  isAvailable: () => boolean;
  // Added in bridge 18.0.1.0.0+; absent on older deployed versions.
  hasActiveChat?: () => boolean;
  // Added in bridge 18.0.1.0.4+.
  isChatOpen?: () => boolean;
  unreadCount?: () => number;
  open: () => Promise<void>;
  setIdentity: (token: string | null) => Promise<void>;
  reset: () => Promise<void>;
  // Added in bridge 18.0.1.3.4+: ends the chat on the server (solves the ticket).
  end?: () => Promise<void>;
};

declare global {
  interface Window {
    clLivechat?: ClLivechatApi;
    clLivechatConfig?: { hideDefaultButton?: boolean };
  }
}

const READY_EVENT = "cl-livechat:ready";
const CHANGE_EVENT = "cl-livechat:change";
const LOAD_EVENT = "cl-livechat:load";
// Remembers that a chat is in progress so the bundle is loaded on the next
// page without waiting for a click, which lets the visitor resume the chat.
const RESUME_KEY = "clLivechatResume";

// Remembers (per browser tab) that the launcher was shown or the chat opened,
// so the launcher stays available on every page. Odoo does not count a chat without a
// first message as active, so its own state cannot be used for this.
const OPENED_KEY = "clLivechatOpened";

let isLoadRequested = false;
let cachedToken: { promise: Promise<string | null>; at: number } | null = null;

export const hasOpenedOdooLiveChat = () => {
  try {
    return window.sessionStorage.getItem(OPENED_KEY) === "1";
  } catch {
    return false;
  }
};

export const markOdooLiveChatOpened = () => {
  try {
    window.sessionStorage.setItem(OPENED_KEY, "1");
  } catch {
    // Storage can be unavailable (private mode); the launcher then only shows on Bimbel.
  }
};

// Set before the page reload that re-checks availability, so the launcher
// reopens the chat right after instead of making the visitor click again.
const AUTO_OPEN_KEY = "clLivechatAutoOpen";

export const setOdooLiveChatAutoOpen = () => {
  try {
    window.sessionStorage.setItem(AUTO_OPEN_KEY, "1");
  } catch {
    // Without storage the visitor just clicks again after the reload.
  }
};

export const consumeOdooLiveChatAutoOpen = (): boolean => {
  try {
    const isSet = window.sessionStorage.getItem(AUTO_OPEN_KEY) === "1";
    window.sessionStorage.removeItem(AUTO_OPEN_KEY);
    return isSet;
  } catch {
    return false;
  }
};

const hasResumeFlag = () => {
  try {
    return window.localStorage.getItem(RESUME_KEY) === "1";
  } catch {
    return false;
  }
};

const setResumeFlag = (isActive: boolean) => {
  try {
    if (isActive) window.localStorage.setItem(RESUME_KEY, "1");
    else window.localStorage.removeItem(RESUME_KEY);
  } catch {
    // Storage can be unavailable (private mode); resuming then needs a click.
  }
};

// The Odoo bundle is only fetched once the visitor asks for the chat (or has a
// chat in progress), so page views alone do not hit the Odoo workers.
export const requestOdooLiveChatLoad = () => {
  if (typeof window === "undefined" || isLoadRequested) return;
  isLoadRequested = true;
  window.dispatchEvent(new Event(LOAD_EVENT));
};

export const subscribeOdooLiveChatLoad = (
  callback: () => void
): (() => void) => {
  if (typeof window === "undefined") return () => undefined;

  if (isLoadRequested || hasResumeFlag()) {
    isLoadRequested = true;
    callback();
    return () => undefined;
  }

  window.addEventListener(LOAD_EVENT, callback, { once: true });
  return () => window.removeEventListener(LOAD_EVENT, callback);
};

// The Odoo bundle is loaded lazily, so run the callback now if the bridge is
// already initialized, otherwise once it announces itself.
export const whenOdooLiveChatReady = (
  callback: (api: ClLivechatApi) => void
): (() => void) => {
  if (typeof window === "undefined") return () => undefined;

  if (window.clLivechat?.isReady()) {
    callback(window.clLivechat);
    return () => undefined;
  }

  const handler = () => window.clLivechat && callback(window.clLivechat);
  window.addEventListener(READY_EVENT, handler, { once: true });
  return () => window.removeEventListener(READY_EVENT, handler);
};

// Calls back once the bridge is ready and on every chat start/end.
export const subscribeOdooLiveChat = (
  callback: (api: ClLivechatApi) => void
): (() => void) => {
  if (typeof window === "undefined") return () => undefined;

  const notify = () => {
    if (!window.clLivechat?.isReady()) return;
    setResumeFlag(Boolean(window.clLivechat.hasActiveChat?.()));
    callback(window.clLivechat);
  };
  notify();
  window.addEventListener(READY_EVENT, notify);
  window.addEventListener(CHANGE_EVENT, notify);
  return () => {
    window.removeEventListener(READY_EVENT, notify);
    window.removeEventListener(CHANGE_EVENT, notify);
  };
};

export const openOdooLiveChat = () => {
  window.clLivechat?.open();
};

const END_CHAT_TIMEOUT_MS = 3000;

// Ends the chat on the server before the browser storage is wiped (the widget
// needs its guest token to tell Odoo). Never blocks the logout for long.
export const endOdooLiveChat = async () => {
  if (typeof window === "undefined" || !window.clLivechat?.end) return;
  try {
    await Promise.race([
      window.clLivechat.end(),
      new Promise((resolve) => setTimeout(resolve, END_CHAT_TIMEOUT_MS))
    ]);
  } catch {
    // The logout must go on even if Odoo is unreachable.
  }
};

export const resetOdooLiveChat = () => {
  if (typeof window !== "undefined") {
    setResumeFlag(false);
    cachedToken = null;
    try {
      window.sessionStorage.removeItem(OPENED_KEY);
    } catch {
      // Ignore unavailable storage.
    }
  }
  window.clLivechat?.reset();
};

const TOKEN_REUSE_MS = 3 * 60 * 1000;

// Shared by the identify component and the launcher, so the token is ready
// (and the welcome message personalized) before the chat opens. The server
// token lives 5 minutes; reuse it for less than that.
export const fetchOdooLiveChatIdentityToken = (): Promise<string | null> => {
  if (cachedToken && Date.now() - cachedToken.at < TOKEN_REUSE_MS) {
    return cachedToken.promise;
  }
  const promise = fetch("/api/odoo-livechat/identity", {
    method: "POST",
    credentials: "same-origin"
  })
    .then(async (response) => {
      if (!response.ok) return null;
      const { token } = await response.json();
      return (token as string | undefined) ?? null;
    })
    .catch(() => null);
  cachedToken = { promise, at: Date.now() };
  promise.then((token) => {
    if (!token && cachedToken?.promise === promise) cachedToken = null;
  });
  return promise;
};

export type OdooLiveChatStatus = {
  available: boolean;
  // null when available; "outside_hours" when closed by the working hours.
  reason: "outside_hours" | "no_operator" | null;
  offline: { messages: string[]; can_leave_message: boolean } | null;
};

// Online/offline without the widget bundle. Null means unknown.
// `fresh` skips the CDN cache, for when the visitor explicitly retries.
export const fetchOdooLiveChatStatus = async (
  fresh = false
): Promise<OdooLiveChatStatus | null> => {
  try {
    const response = await fetch(
      `/api/odoo-livechat/status${fresh ? "?fresh=1" : ""}`,
      fresh ? { cache: "no-store" } : undefined
    );
    if (!response.ok) return null;
    const status = await response.json();
    return typeof status?.available === "boolean" ? status : null;
  } catch {
    return null;
  }
};

// First name from the identity token claims (readable, not trusted here: only
// used to greet the visitor).
export const getOdooLiveChatFirstName = async (): Promise<string> => {
  const token = await fetchOdooLiveChatIdentityToken();
  if (!token) return "";
  try {
    const [payload] = token.split(".");
    const { name } = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
    );
    return String(name || "")
      .trim()
      .split(/\s+/)[0];
  } catch {
    return "";
  }
};

export type LeaveMessageResult = "ok" | "rate_limited" | "failed";

export const leaveOdooLiveChatMessage = async (
  message: string
): Promise<LeaveMessageResult> => {
  try {
    const response = await fetch("/api/odoo-livechat/leave-message", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message })
    });
    if (response.ok) return "ok";
    return response.status === 429 ? "rate_limited" : "failed";
  } catch {
    return "failed";
  }
};
